import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white hover:bg-primary-hover font-normal',
        secondary:
          'bg-white dark:bg-transparent text-foreground hover:bg-hover dark:hover:bg-hover border border-border',
        outline:
          'border border-border bg-transparent dark:bg-transparent text-foreground hover:bg-hover dark:hover:bg-hover',
        ghost:
          'border-none bg-transparent dark:bg-transparent text-foreground hover:bg-hover dark:hover:bg-hover rounded-lg',
      },
      size: {
        default: 'h-9 px-4 [&_svg]:size-5',
        sm: 'h-8 px-3 text-xs',
        xs: 'h-7 px-2 text-sm',
        lg: 'h-10 px-4 md:px-8',
        icon: 'h-9 w-9 [&_svg]:size-6',
        'icon-sm': 'h-8 w-8 [&_svg]:size-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
